import { useMemo } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Info } from 'lucide-react'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell
} from '@/components/ui/table'

function formatCurrency(value) {
  if (value === null || value === undefined) return '—'
  const prefix = value < 0 ? '-' : ''
  return prefix + new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.abs(value))
}

function formatMonth(dateStr) {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

function formatPercent(value) {
  if (value === null || value === undefined || !isFinite(value)) return '—'
  return `${value.toFixed(1)}%`
}

export default function MonthlyPnLTable({ trendData, isLoading = false }) {
  const rows = useMemo(() => {
    if (!trendData || trendData.length === 0) return []
    return trendData.map(point => {
      const revenue = Number(point.revenue) || 0
      const fixed = Number(point.total_fixed_costs) || Number(point.fixed_costs) || 0
      const variable = Number(point.total_variable_costs) || Number(point.variable_costs) || 0
      const totalCosts = Number(point.total_costs) || (fixed + variable)
      const profitLoss = revenue - totalCosts
      const margin = revenue > 0 ? (profitLoss / revenue) * 100 : null
      return {
        month: point.period_start || point.month,
        revenue,
        fixed,
        variable,
        totalCosts,
        profitLoss,
        margin
      }
    })
  }, [trendData])

  const totals = useMemo(() => {
    if (rows.length === 0) return null
    const t = rows.reduce((acc, r) => ({
      revenue: acc.revenue + r.revenue,
      fixed: acc.fixed + r.fixed,
      variable: acc.variable + r.variable,
      totalCosts: acc.totalCosts + r.totalCosts,
      profitLoss: acc.profitLoss + r.profitLoss
    }), { revenue: 0, fixed: 0, variable: 0, totalCosts: 0, profitLoss: 0 })
    t.margin = t.revenue > 0 ? (t.profitLoss / t.revenue) * 100 : null
    return t
  }, [rows])

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Monthly Profit & Loss</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (rows.length === 0) return null

  const headers = [
    { label: 'Month', align: 'left', minW: 'min-w-[100px]' },
    { label: 'Revenue', align: 'right', tip: 'Total sales income from all products sold' },
    { label: 'Fixed Costs', align: 'right', tip: 'Costs that stay the same regardless of production — rent, insurance, energy base load, etc.' },
    { label: 'Variable Costs', align: 'right', tip: 'Costs that scale with production — substrate, spawn, shiitake logs, energy overage' },
    { label: 'Total Costs', align: 'right', tip: 'Fixed costs plus variable costs' },
    { label: 'Profit / Loss', align: 'right', tip: 'Revenue minus total costs. Green means profit, red means loss' },
    { label: 'Margin', align: 'right', tip: 'Profit or loss as a percentage of revenue' },
  ]

  return (
    <TooltipProvider delayDuration={300}>
    <Card>
      <CardHeader>
        <CardTitle>Monthly Profit & Loss</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              {headers.map(h => (
                <TableHead key={h.label} className={`${h.align === 'right' ? 'text-right' : ''} ${h.minW || ''}`}>
                  <div className={`flex items-center gap-1 ${h.align === 'right' ? 'justify-end' : ''}`}>
                    {h.label}
                    {h.tip && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help flex-shrink-0" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-xs">
                          {h.tip}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.month}>
                <TableCell className="font-medium text-sm">{formatMonth(row.month)}</TableCell>
                <TableCell className="text-right text-sm">{formatCurrency(row.revenue)}</TableCell>
                <TableCell className="text-right text-sm">{formatCurrency(row.fixed)}</TableCell>
                <TableCell className="text-right text-sm">{formatCurrency(row.variable)}</TableCell>
                <TableCell className="text-right text-sm">{formatCurrency(row.totalCosts)}</TableCell>
                <TableCell className={`text-right text-sm font-semibold ${row.profitLoss >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {formatCurrency(row.profitLoss)}
                </TableCell>
                <TableCell className={`text-right text-sm ${row.margin !== null && row.margin >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {formatPercent(row.margin)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          {totals && (
            <TableFooter>
              <TableRow>
                <TableCell className="font-semibold">Total</TableCell>
                <TableCell className="text-right font-semibold text-sm">{formatCurrency(totals.revenue)}</TableCell>
                <TableCell className="text-right font-semibold text-sm">{formatCurrency(totals.fixed)}</TableCell>
                <TableCell className="text-right font-semibold text-sm">{formatCurrency(totals.variable)}</TableCell>
                <TableCell className="text-right font-semibold text-sm">{formatCurrency(totals.totalCosts)}</TableCell>
                <TableCell className={`text-right font-bold text-sm ${totals.profitLoss >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {formatCurrency(totals.profitLoss)}
                </TableCell>
                <TableCell className={`text-right font-semibold text-sm ${totals.margin !== null && totals.margin >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {formatPercent(totals.margin)}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </CardContent>
    </Card>
    </TooltipProvider>
  )
}
